// The tutorial's chapters (DEMO.md "A tutorial with chapters").
//
// The demo is a tutorial of chapters a person can revisit at will, each one
// standalone and identical on every visit.
//
// So a chapter is not a slice of one long walk. It is a self-contained thing
// with its own beginning: entering one rebuilds the world from the seed (see
// SimProvider's tutorial mode) and sets its own clock, which is what makes it
// identical every time and free of whatever the visitor did last time.
//
// Copy comes from the league's own data through the screens; nothing here
// names a sport.

import type { JumpPreset } from '../data/SimProvider';
import { storageKey } from '../data/storage';

/** An action a screen reports when the visitor does the thing a card asked for. */
export type TourAction = 'open-league' | 'health-fix' | 'add' | 'claim' | 'react' | 'pick';

export interface Moment {
  id: string;
  title: string;
  body: string;
  tryIt: string;
  /** Where the moment lands. The demo world's league ids are fixed. */
  route: string;
  /** The clock preset to set on arrival; null keeps the clock where it is. */
  clock: JumpPreset | null;
  /** Run the clock at 30× on arrival (game day). */
  playing?: boolean;
  /** A CSS selector for the spotlight; null for a card with nothing to point at. */
  anchor: string | null;
  /** The action that earns a "Nice." */
  done?: TourAction;
  /** Opens the chat drawer on arrival. */
  openChat?: boolean;
  /** An extra button on the card that moves the clock. */
  jump?: { label: string; preset: JumpPreset };
}

export interface Chapter {
  id: string;
  title: string;
  /** One line on the index card: what this chapter shows. */
  blurb: string;
  moments: readonly Moment[];
}

export const CHAPTERS: readonly Chapter[] = [
  {
    id: 'game-day',
    title: 'Game day',
    blurb: 'Sunday afternoon: a lineup problem, a live matchup, and the league reacting.',
    moments: [
      {
        id: 'your-leagues',
        title: 'Your leagues',
        body: 'Every league you are in, and the one thing each needs from you. Three leagues here, one of them drafting tonight.',
        tryIt: 'Open the first league.',
        route: '/demo/leagues',
        clock: 'sun-mid',
        anchor: '.fsp-league-card',
        done: 'open-league',
      },
      {
        id: 'lineup-problem',
        title: 'A problem in your lineup',
        body: 'Sunday afternoon, and someone you are starting is not playing. The health bar says so before you have to go looking, and takes you to the slot. Who plays it is your call.',
        tryIt: 'Open the slot from the health bar, then pick who fills it.',
        route: '/l/friends',
        clock: 'sun-mid',
        anchor: '.fsp-health',
        done: 'health-fix',
      },
      {
        id: 'live',
        title: 'Every point, live',
        body: 'Scores land the moment a play happens. Watch the numbers glow; the clock here is running at thirty times speed.',
        tryIt: 'Open the play feed and watch the win bar move.',
        route: '/l/friends/matchup',
        clock: 'sun-mid',
        playing: true,
        anchor: 'section[aria-label="Plays"], section[aria-label="More"], .fsp-winbar',
      },
    ],
  },
  {
    id: 'the-wire',
    title: 'Working the wire',
    blurb: 'Who is worth adding, what happened to your guys, and how sealed bids resolve.',
    moments: [
      {
        id: 'adding',
        title: "Who's worth adding",
        body: 'Every free agent, in order of points a game. Add is two taps, and the roster is never left illegal.',
        tryIt: 'Add someone from the list.',
        route: '/l/friends/players',
        clock: 'sun-mid',
        anchor: 'section[aria-label="List"]',
        done: 'add',
      },
      {
        id: 'news',
        title: "What's up with your guys",
        body: "Status changes, practice reports, and every move in the league, one line each, yours first. Never a wall of everyone else's news.",
        tryIt: 'Switch the filter to All.',
        route: '/l/friends/news',
        clock: 'sun-mid',
        anchor: 'section[aria-label="List"]',
      },
      {
        id: 'wire',
        title: 'The waiver wire',
        body: 'Bids are sealed until Wednesday. Put a claim in, then jump the clock to see it resolve, with a sentence for every claim, won or lost.',
        tryIt: 'Claim someone, then press the jump button here.',
        route: '/l/friends/waivers',
        clock: 'sun-mid',
        anchor: '.fsp-claim-card',
        done: 'claim',
        jump: { label: 'Jump to Wednesday 3:05 AM', preset: 'wed-waivers' },
      },
    ],
  },
  {
    id: 'a-deal',
    title: 'Making a deal',
    blurb: 'An offer with an honest read-out, and the block where most trades actually start.',
    moments: [
      {
        id: 'trade',
        title: 'A trade, with the truth',
        body: 'Dave wants your running back. The read-out tells you what each side gives, what your lineup does after, and what you would be short at. It is never a grade.',
        tryIt: "Open Dave's offer and read the three lines.",
        route: '/l/friends/trades',
        clock: 'sun-mid',
        anchor: 'main .fsp-region',
      },
      {
        id: 'block',
        title: 'The trade block',
        body: 'Mark a player available and say what you want back, in your own words. Most trades start here rather than with someone typing into the void.',
        tryIt: 'Read what other managers are shopping, then hit Offer on one.',
        route: '/l/friends/trades?tab=block',
        clock: null,
        // The block's panel: the Market segment's on a phone or a tablet,
        // beside the offers on a desktop (B-388).
        anchor: '.fsp-tr-block-panel',
      },
    ],
  },
  {
    id: 'trash-talk',
    title: 'Talking trash',
    blurb: 'One channel for the whole league, and a nudge when you cannot think of anything.',
    moments: [
      {
        id: 'talk',
        title: 'The league talks',
        body: 'One channel for everything: the trash talk, the trade, the waiver results, the finals, the awards. Tap a message for the reactions.',
        tryIt: 'React to anything.',
        route: '/l/friends',
        clock: 'wed-waivers',
        anchor: 'button[aria-label="League chat"]',
        done: 'react',
        openChat: true,
      },
      {
        id: 'starters',
        title: 'When you have got nothing',
        body: 'The composer offers a few openers about your own matchup, every one of them true: the lead, what is left to play, a bench that outscored the starters. You edit it and you own it. The app never posts a taunt itself.',
        tryIt: 'Tap a starter and make it worse.',
        route: '/l/friends',
        clock: 'wed-waivers',
        anchor: 'button[aria-label="League chat"]',
        openChat: true,
      },
    ],
  },
  {
    id: 'draft-night',
    title: 'Draft night',
    blurb: 'A live room with managers who have opinions, and the grades the morning after.',
    moments: [
      {
        id: 'draft',
        title: 'The room',
        body: 'Family Cup drafts tonight. Two seats are empty; managers with personalities fill them and talk trash. The clock is running.',
        tryIt: 'Make your pick when the clock says it is you.',
        route: '/l/family/draft',
        clock: 'sun-night',
        playing: true,
        anchor: '.fsp-draft-board',
        done: 'pick',
      },
      {
        id: 'draft-recap',
        title: 'Graded, the morning after',
        body: 'Every team gets a grade, and every grade points at a pick and a number. Steals and reaches are measured against where players normally go, so the argument starts with evidence.',
        tryIt: 'Find the biggest reach, then see who got the A.',
        route: '/l/friends/draft-recap',
        clock: null,
        anchor: '.fsp-grade-letter',
      },
    ],
  },
  {
    id: 'the-season',
    title: 'The season',
    blurb: 'Where you stand, who you play, and what the bracket looks like from here.',
    moments: [
      {
        id: 'standings',
        title: 'Where you stand',
        body: 'Records, points, streaks, and the playoff line. Every row opens that manager’s trophy room.',
        tryIt: 'Tap a team.',
        route: '/l/friends/standings',
        clock: 'final',
        anchor: 'section[aria-label="List"]',
      },
      {
        id: 'bracket',
        title: 'The bracket',
        body: 'Projected from where everyone stands right now, so a mid-table team can see exactly what it needs. It goes live when the playoff weeks arrive, with byes, reseeding, and a consolation bracket if the league wants one.',
        tryIt: 'Switch back to Schedule and scan the weeks.',
        route: '/l/friends/schedule?tab=playoffs',
        clock: 'final',
        anchor: '.fsp-segments',
      },
    ],
  },
  {
    id: 'bragging-rights',
    title: 'Bragging rights',
    blurb: 'The Tuesday read, the awards nobody asked for, and the records nobody forgets.',
    moments: [
      {
        id: 'recap',
        title: 'Tuesday morning',
        body: 'The week, written the moment the last game ends: the upset, the blowout, who left points on the bench, who moved. Your own week in one sentence is on Home.',
        tryIt: 'Read the games. Tap one.',
        route: '/l/friends/recap',
        clock: 'final',
        anchor: '.fsp-recap-headline',
      },
      {
        id: 'awards',
        title: 'Ten awards a week',
        body: 'Funny on purpose, posted to chat, and every one backed by a number. Trophies and badges pile up in a room you can visit for anyone.',
        tryIt: "Open a card, then a rival's trophy room.",
        route: '/l/friends/awards',
        clock: 'final',
        anchor: '.fsp-awards-grid',
      },
      {
        id: 'record-book',
        title: 'The record book',
        body: 'Champions by season, the all-time table, and the records nobody forgets: highest week, biggest blowout, longest streak. This is the screen an old league opens to settle who is actually the best.',
        tryIt: 'Find the highest week anyone has ever put up.',
        route: '/l/friends/history',
        clock: 'final',
        anchor: '.fsp-records',
      },
    ],
  },
  {
    id: 'make-it-yours',
    title: 'Make it yours',
    blurb: 'Rules anyone can read, your colors, and the columns if you want them.',
    moments: [
      {
        id: 'rules',
        title: 'Rules in plain English',
        body: 'Every rule is a sentence anyone can read. The commissioner changes them with a reason, and every version is kept.',
        tryIt: 'Skim the scoring. Nothing here needs a glossary.',
        route: '/l/friends/rules',
        clock: null,
        anchor: '.fsp-rules-section',
      },
      {
        id: 'appearance',
        title: 'Your colors',
        body: "Your favorite team's colors, light or dark, denser or roomier. Casual by default, because most managers never want more than this.",
        tryIt: 'Pick a team and watch the app change.',
        // The sheet over the screen before (B-399): its colors' card.
        route: '/account/appearance',
        clock: null,
        anchor: '[data-tour="appearance"]',
      },
      {
        id: 'pro-mode',
        title: 'Pro mode',
        body: 'One switch. Season points, the last-three average, ownership and its trend on every player row, and the projected favorite on every future week.',
        tryIt: 'Turn it on, then look at Players again.',
        route: '/account/appearance',
        clock: null,
        anchor: '[data-tour="pro-mode"]',
      },
    ],
  },
];

export function chapterById(id: string): Chapter | null {
  return CHAPTERS.find((c) => c.id === id) ?? null;
}

// ── Which chapters someone has seen ────────────────────────────────────────
//
// Which chapters were viewed is tracked only for an account, never for a
// visitor, and nothing carries over when a visitor signs up.
//
// So progress is keyed to the account and lives in localStorage with it. A
// visitor has no key and nothing is written; signing up starts empty, because
// there was never anything to carry.

const SEEN_PREFIX = storageKey('tutorial.seen.');

export function seenChapters(userId: string | null): Set<string> {
  if (!userId) return new Set();
  try {
    const raw = localStorage.getItem(SEEN_PREFIX + userId);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(
      Array.isArray(list) ? list.filter((v): v is string => typeof v === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

export function markChapterSeen(userId: string | null, chapterId: string): void {
  if (!userId) return;
  try {
    const next = seenChapters(userId);
    next.add(chapterId);
    localStorage.setItem(SEEN_PREFIX + userId, JSON.stringify([...next]));
  } catch {
    // Storage blocked: the tutorial still works, it just does not remember.
  }
}

const EVENT = 'fsp:tour';

/** Screens call this when the visitor does the thing a card asked for. Cheap; a no-op with no tutorial. */
export function tourDone(action: TourAction): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: action }));
}

export function onTourAction(handler: (action: TourAction) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<TourAction>).detail);
  window.addEventListener(EVENT, listener);
  return () => window.removeEventListener(EVENT, listener);
}
